import { Injectable } from '@angular/core';

/**
 * Shape of a city entry returned by `ReferenceDao.getCities()` (`/ref/city`).
 * Swisslife relies on the official INSEE / La Poste postal codes, so each
 * reference entry pairs a `city` label with its normalized `zipCode`.
 */
export interface CityRef {
  city: string;
  zipCode: string;
  [key: string]: any;
}

/**
 * Mutualizes the Swisslife-specific "code postal + ville" logic that used to be
 * duplicated across the subscription funnel (birth-info-form, address-form,
 * pr-input-map, owner-tax-residence).
 *
 * Swisslife forces a constrained zip/city pair (official INSEE/La Poste data)
 * instead of free-text / Google Maps autocomplete, hence the dedicated helpers.
 */
@Injectable({ providedIn: 'root' })
export class SwisslifeLocationService {
  /** Value of `product.parameters.partnerFlux` identifying a Swisslife flux. */
  static readonly SWISSLIFE_PARTNER_FLUX = 'SWISSLIFE';

  /** True when the given pledge contains at least one Swisslife product. */
  hasSwisslifeProduct(pledge: any): boolean {
    return !!pledge?.products
      ?.filter((product: any) => !!product?.parameters?.partnerFlux)
      ?.some(
        (product: any) =>
          product?.parameters?.partnerFlux ===
          SwisslifeLocationService.SWISSLIFE_PARTNER_FLUX,
      );
  }

  /**
   * Filter the reference city list by a zip-code prefix.
   * This is the behaviour the dropdowns currently rely on: once a CGP types a
   * (partial) zip code, only the matching cities remain selectable.
   */
  filterCitiesByZipCode(cities: CityRef[], zipCode: string): CityRef[] {
    const prefix = (zipCode || '').trim();
    if (!prefix) {
      return [...(cities ?? [])];
    }
    return (cities ?? []).filter(city => city?.zipCode?.startsWith(prefix));
  }

  /**
   * Search the reference city list by city NAME **or** zip code.
   * Powers the "Je ne trouve pas ma ville / mon code postal" finder modal:
   * a CGP who only knows the city name can look up the official Swisslife zip
   * code (and vice-versa). Results are capped to keep the list rendering cheap.
   */
  searchCities(cities: CityRef[], term: string, limit = 50): CityRef[] {
    const query = this.normalize(term);

    if (!query) {
      return (cities ?? []).slice(0, limit);
    }

    return (cities ?? [])
      .filter(city => {
        const name = this.normalize(city?.city);
        const zip = (city?.zipCode || '').toLowerCase();
        return name.includes(query) || zip.startsWith(query);
      })
      .slice(0, limit);
  }

  /** Combining diacritical marks (U+0300–U+036F), built without literal glyphs. */
  private static readonly DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

  /** Lower-cased, accent-insensitive form used for tolerant matching. */
  private normalize(value: string): string {
    return (value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(SwisslifeLocationService.DIACRITICS, '');
  }
}
